import { useMemo } from 'react';
import { Box, Drawer, List, ListItemButton, Typography, Divider, IconButton } from '@mui/material';
import { IconApps, IconHelp, IconHome, IconLogout, IconReport, IconSettings, IconX } from '@tabler/icons-react';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { LEFT_NAV_DARK_BLUE, DRAWER_WIDTH } from 'common/Constant';
import { useSelector } from 'react-redux';
import { hasSettingsAccess } from 'utils/common';
import { useLocation, useNavigate } from 'react-router-dom';
import routePaths from 'routes/routePaths';
import UserMenu from 'features/landing/navigation/top/userMenu';

const navItemButtonStyle = {
  padding: 1,
  borderRadius: 2,
  color: '#FFFFFF',
  '&:hover': {
    bgcolor: '#D8E6FF',
    color: '#000'
  },
  '&.Mui-selected': {
    bgcolor: '#046BBE',
    color: '#FFFFFF'
  },
  '&.Mui-selected:hover': {
    bgcolor: '#046BBE',
    color: '#FFFFFF'
  }
};

const navItemIconStyle = {
  color: 'inherit',
  marginRight: '8px',
  width: '24px',
  height: '24px'
};

const navItemTextStyle = {
  color: 'inherit',
  fontFamily: 'Noto Sans',
  fontSize: '16px',
  fontStyle: 'normal',
  leadingTrim: 'both',
  textEdge: 'cap',
  fontWeight: '400',
  lineHeight: '24px'            
};

const listSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
};

const SideNavBar = ({ currentTenantCd, open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useSelector((state) => state.user.permissions);
  const externalUser = useSelector((state) => state.user.external);
  const apps = useSelector((state) => state.user.apps);
  const logoCacheBuster = useSelector((state) => state.whiteLabeling?.logoCacheBuster || '');

  const isSettingsVisible = useMemo(
    () => hasSettingsAccess({ permissions, externalUser, apps }),
    [permissions, externalUser, apps]
  );

  const isFullMenu = open !== undefined; // Tablet & Mobile Viewports

  const handleNavClick = (path) => {
    navigate(path);
    onClose?.();
  };

  return (
    <Drawer
      variant={isFullMenu ? "temporary" : "permanent"}
      anchor="left"
      open={open}
      onClose={onClose}
      keepMounted={isFullMenu}
      sx={{
        display: isFullMenu ? 'block' : { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: isFullMenu ? '100%' : `${DRAWER_WIDTH}px`,
          px: isFullMenu ? { xs: 2, sm: 6 } : 3,
          py: 3,
          paddingBottom: isFullMenu ? 3 : 6,
          backgroundColor: LEFT_NAV_DARK_BLUE,
          borderRight: 'none'
        }
      }}
    >
      <Box 
        sx={{ 
          mb: isFullMenu ? 3 : 8,
          display: 'flex',
          justifyContent: isFullMenu ? 'space-between' : 'center',
          alignItems: 'center' 
        }}
      >
        <Box
          component="img"
          sx={{ width: isFullMenu ? { xs: '109px', sm: '134px' } : '176px' }}
          src={`${AppConfig.FO_INITIAL_URL}/open-api/logo/v2/tenant/${currentTenantCd}/dark?cb=${logoCacheBuster}`}
          alt="BST"
          onClick={() => handleNavClick(routePaths.newHome)}
        />
        {isFullMenu && (
          <IconButton
            aria-label="Close side navigation drawer"
            onClick={onClose}
            edge="end"
            sx={{
              color: '#FFF',
              '&:hover svg': { color: '#000000' }
            }}
          >
            <IconX size={24} />
          </IconButton>
        )}
      </Box>
      {isFullMenu && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', gap: 4 }}>
          <UserMenu onSideNavBar />
          <Divider sx={{ borderColor: '#71767A', borderWidth: 1, mb: 4 }} />
        </Box>
      )}
      <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%', gap: 5 }}>
        <List disablePadding sx={listSx}>
          <ListItemButton
            onClick={() => handleNavClick(routePaths.frontOffice)}
            sx={navItemButtonStyle}
            selected={location.pathname === routePaths.frontOffice}
          >
            <IconHome style={navItemIconStyle} />
            <Typography variant="body1" sx={navItemTextStyle}>
              Home
            </Typography>
          </ListItemButton>
          <ListItemButton
            onClick={() => handleNavClick(routePaths.reports)}
            sx={navItemButtonStyle}
            selected={location.pathname === routePaths.reports}
          >
            <IconReport style={navItemIconStyle} />
            <Typography variant="body1" sx={navItemTextStyle}>
              Reports
            </Typography>
          </ListItemButton>
        </List>
        <List disablePadding sx={{ ...listSx, color: '#FFFFFF', marginTop: isFullMenu ? 0 : 'auto' }}>
          <ListItemButton
            onClick={() => handleNavClick(routePaths.applications)}
            sx={navItemButtonStyle}
            selected={location.pathname === routePaths.applications}
          >
            <IconApps style={navItemIconStyle} />
            <Typography variant="body1" sx={navItemTextStyle}>
              Applications
            </Typography>
          </ListItemButton>
          {isSettingsVisible && 
            <ListItemButton
              onClick={() => handleNavClick(routePaths.settings)}
              selected={location.pathname === routePaths.settings}
              sx={navItemButtonStyle}
            >
              <IconSettings style={navItemIconStyle} />
              <Typography variant="body1" sx={navItemTextStyle}>
                Settings
              </Typography>
            </ListItemButton>
          }
          <ListItemButton
            onClick={() => {
              axios
                .get(AppConfig.FO_INITIAL_URL + `/external/zendesk`)
                .then((res) => {
                  window.open('https://benefitsscience.zendesk.com/access/jwt?jwt=' + res.data);
                })
                .catch((err) => {});
            }}
            sx={navItemButtonStyle}
          >
            <IconHelp style={navItemIconStyle} />
            <Typography variant="body1" sx={navItemTextStyle}>
              Help and Support
            </Typography>
          </ListItemButton>
          <ListItemButton
            onClick={() => handleNavClick(routePaths.logout)}
            sx={navItemButtonStyle}
          >
            <IconLogout style={navItemIconStyle} />
            <Typography variant="body1" sx={navItemTextStyle}>
              Sign Out
            </Typography>
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
};

export default SideNavBar;